import React, { useState } from "react";
import { Gem, Sparkles, Award, Scale, Flame, Eye, X, ZoomIn, Download, FileText, MessageCircle, Globe, Instagram, MapPin, Users } from "lucide-react";

const IMG_DIAMOND = "https://static.prod-images.emergentagent.com/jobs/44e922b4-6326-4e9e-a61b-a036e60b36dc/images/ff3643cdc709a892584a22be741999c3ea8a0aa0e3cd31da5a5eacf26cfc3f4d.png";
const IMG_BOWTIE = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/8944x9wb_MicrosoftTeams-image%20%2843%29.jpeg";
const IMG_WINDOWING = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/ec06sify_Diamond-Windowing-in-Emerald-Cut-1024x555.png.webp";

export default function GemsPage({ onDownload, documents }) {
  const giaDoc = documents?.find((d) => d.id === "doc-gia-example");
  const [zoomedImg, setZoomedImg] = useState(null);
  return (
    <div data-testid="view-gems" className="max-w-5xl">
      <Hero />

      {/* DIAMONDS */}
      <StoneHeader code="01" name="Diamants" tag="Hardness 10 · Sp. gravity 3.52" />

      <Section eyebrow="Prix" title="Comment se cote un diamant : De Beers, Rapaport, RapNet" index="01">
        <p>
          Contrairement à l'or, le diamant n'a pas de cours spot public. Trois référentiels dominent la formation des prix :
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <RefCard title="De Beers" sub="Le producteur historique" body="Contrôlait ~85 % du marché au XXᵉ siècle. Toujours prescripteur via ses sights (ventes trimestrielles à 80 sightholders agréés). Les prix DeBeers servent de plancher wholesale pour le rough." />
          <RefCard title="Rapaport Price List" sub="Le barème hebdomadaire" body="Publié tous les vendredis par Martin Rapaport depuis 1978. Grille de prix par carat × couleur × clarté pour diamants taillés ronds. Référence N°1 pour négocier : 'discount 25 % du Rap'." />
          <RefCard title="RapNet" sub="La plateforme B2B" body="Place de marché en ligne Rapaport. Plus de 1,6 million de diamants listés en temps réel par ~14 000 traders. Permet de connaître le vrai prix de marché et non la théorie." />
        </div>
        <Callout icon={Scale} title="Comment se lit un prix diamant">
          Le prix se négocie toujours en <strong className="text-white">pourcentage de discount du Rapaport</strong>.
          Exemple : un diamant rond 1 ct, couleur G, clarté VS2 coté 8 000 $/ct au Rap, vendu à "−22 % du Rap" =
          6 240 $/ct = 6 240 $ pour la pierre. Négocier sans connaître son discount = perdre 15–25 %.
        </Callout>
      </Section>

      <Section eyebrow="Les 4C" title="Les 4 critères qui font le prix d'un diamant" index="02">
        <p>
          Définis par la GIA dans les années 50, les 4C sont le langage universel du diamant : <strong className="text-white">Carat, Color, Clarity, Cut</strong>.
          Une pierre mal notée sur un seul C peut valoir 40 % moins qu'une pierre "parfaite" de même carat.
        </p>

        {/* Carat */}
        <SubSection title="Carat — le poids" icon={Scale}>
          <p>1 carat = 0,20 gramme. Le prix par carat explose avec la taille : un 2 ct ne vaut pas 2× un 1 ct, mais souvent 2,5–3× à caractéristiques égales (rareté).</p>
          <div className="flex items-end gap-3 mt-6 flex-wrap">
            {[{ ct: 0.5, size: 28 }, { ct: 1, size: 40 }, { ct: 1.5, size: 50 }, { ct: 2, size: 60 }, { ct: 3, size: 72 }, { ct: 5, size: 90 }].map((d) => (
              <div key={d.ct} className="text-center">
                <div className="rounded-full mx-auto" style={{ width: d.size, height: d.size, background: "radial-gradient(circle at 30% 30%, #ffffff 0%, #d4d4d4 40%, #737373 100%)", boxShadow: "0 0 20px rgba(212,175,55,0.15)" }} />
                <div className="mt-2 text-xs font-mono text-[#A1A1AA]">{d.ct} ct</div>
              </div>
            ))}
          </div>
        </SubSection>

        {/* Color */}
        <SubSection title="Color — la couleur (absence de couleur)" icon={Sparkles}>
          <p>Un diamant blanc parfait est <strong className="text-white">incolore</strong>. L'échelle GIA va de D (incolore, très rare) à Z (jaune marqué). Chaque lettre = un prix très différent.</p>
          <div className="mt-6 border border-white/10 overflow-hidden">
            <div className="grid grid-cols-11 text-center font-mono text-xs">
              {[
                { l: "D", t: "#fafafa", label: "Incolore" },
                { l: "E", t: "#fafaf5", label: "" },
                { l: "F", t: "#f9f8ea", label: "" },
                { l: "G", t: "#f6f2d9", label: "Presque incolore" },
                { l: "H", t: "#f2edc6", label: "" },
                { l: "I", t: "#eee6b0", label: "" },
                { l: "J", t: "#ecdf97", label: "" },
                { l: "K", t: "#e9d67d", label: "Nuance faible" },
                { l: "L", t: "#e5cc61", label: "" },
                { l: "M", t: "#e0c248", label: "" },
                { l: "Z", t: "#d4a828", label: "Jaune clair" },
              ].map((c) => (
                <div key={c.l} className="py-4 border-r border-white/10 last:border-r-0 text-black" style={{ backgroundColor: c.t }}>
                  <div className="font-serif text-lg">{c.l}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] border-t border-white/10">
              <div className="px-3 py-3 border-r border-white/10">D–F · Incolore</div>
              <div className="px-3 py-3 border-r border-white/10">G–J · Presque incolore</div>
              <div className="px-3 py-3 border-r border-white/10">K–M · Teinte faible</div>
              <div className="px-3 py-3">N–Z · Teinte visible</div>
            </div>
          </div>
        </SubSection>

        {/* Clarity */}
        <SubSection title="Clarity — la pureté" icon={Eye}>
          <p>Inclusions internes et imperfections externes. 11 grades, du "parfait pur" (FL/IF) aux inclusions visibles à l'œil nu (I1/I2/I3).</p>
          <div className="mt-6 space-y-2">
            {[
              { g: "FL / IF", label: "Flawless / Internally Flawless", desc: "Aucune inclusion, même sous 10×.", premium: "Rareté extrême — +30 à +60 % de prime" },
              { g: "VVS1 / VVS2", label: "Very Very Slightly Included", desc: "Inclusions microscopiques, difficiles à voir sous 10×.", premium: "Haut de gamme" },
              { g: "VS1 / VS2", label: "Very Slightly Included", desc: "Inclusions visibles sous 10× mais imperceptibles à l'œil nu.", premium: "Meilleur rapport qualité/prix" },
              { g: "SI1 / SI2", label: "Slightly Included", desc: "Inclusions évidentes sous 10×, parfois visibles à l'œil.", premium: "Option budget" },
              { g: "I1 / I2 / I3", label: "Included", desc: "Inclusions visibles à l'œil nu, impact sur la brillance.", premium: "Éviter pour la joaillerie premium" },
            ].map((c) => (
              <div key={c.g} className="grid grid-cols-12 border border-white/10 bg-[#0c0c0c] p-5 hover:border-white/25 transition-colors">
                <div className="col-span-12 md:col-span-2 font-mono text-[#D4AF37]">{c.g}</div>
                <div className="col-span-12 md:col-span-5 font-serif text-white mt-2 md:mt-0">{c.label}</div>
                <div className="col-span-12 md:col-span-5 text-xs text-[#A1A1AA] mt-2 md:mt-0">{c.desc}<div className="text-[#D4AF37] mt-1">{c.premium}</div></div>
              </div>
            ))}
          </div>
        </SubSection>

        {/* Cut */}
        <SubSection title="Cut — les tailles (formes)" icon={Sparkles}>
          <p>La taille influe sur la brillance (restitution de la lumière). 6 formes dominent le marché haut de gamme :</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            <CutCard name="Round Brilliant" desc="58 facettes. 75 % du marché. Brillance maximale." shape="round" />
            <CutCard name="Princess" desc="Taille carrée à angles vifs. Moderne, polyvalente." shape="princess" />
            <CutCard name="Emerald" desc="Taille rectangulaire à degrés. Clarté plus exigeante." shape="emerald" />
            <CutCard name="Cushion" desc="Carré arrondi, 'coussin'. Rendu vintage, très demandé." shape="cushion" />
            <CutCard name="Radiant" desc="Rectangulaire, facettes brillantes. Éclat fort." shape="radiant" />
            <CutCard name="Marquise" desc="Ellipse pointue. Allonge le doigt, effet 'grande pierre'." shape="marquise" />
          </div>
        </SubSection>
      </Section>

      {/* Cut defects — Windowing & Bow-tie */}
      <Section eyebrow="Défauts de taille" title="Windowing & Bow-tie : les pièges à reconnaître" index="02·B">
        <p>
          Deux défauts de taille décotent immédiatement un diamant, quel que soit son poids ou sa pureté.
          Un intermédiaire qui les repère à l'œil nu <strong className="text-white">évite à son client 20 à 40 % de surpaiement</strong>.
          Cliquez sur chaque photo pour voir le détail.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {/* Windowing */}
          <div className="border border-white/10 bg-[#0c0c0c]">
            <div
              className="aspect-[16/9] bg-[#141414] overflow-hidden relative group cursor-zoom-in"
              onClick={() => setZoomedImg({ src: IMG_WINDOWING, alt: "Windowing dans un diamant Emerald Cut" })}
              data-testid="open-windowing-zoom"
            >
              <img src={IMG_WINDOWING} alt="Windowing effect" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 border border-[#D4AF37]/60 px-4 py-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  <ZoomIn className="h-3.5 w-3.5" /> Agrandir la photo
                </div>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Défaut 01 · Windowing</span>
              </div>
              <h3 className="font-serif text-xl mt-3 text-white">Des facettes noires qui « mangent » le diamant</h3>
              <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                Le <strong className="text-white">windowing</strong> apparaît lorsque des <strong className="text-white">facettes noires</strong> se forment dans la pierre :
                au lieu de renvoyer la lumière, certaines facettes restent sombres, éteintes, comme des trous noirs. C'est le signe d'une
                <strong className="text-white"> taille mal calculée</strong> (angles incorrects, proportions déséquilibrées). La pierre perd son feu,
                son éclat, et sa valeur chute immédiatement.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-[#A1A1AA]">
                <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span>Visible sur les <span className="text-white">Emerald, Asscher, Oval, Princess</span> mal taillés (voir photo : diamant de gauche).</span></li>
                <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span>Décote commerciale : <span className="text-white">−15 à −30 %</span> par rapport à une taille propre (diamant de droite).</span></li>
                <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span>Test : en lumière naturelle, comparez deux pierres côte à côte — les zones noires sautent aux yeux.</span></li>
              </ul>
            </div>
          </div>

          {/* Bow-tie */}
          <div className="border border-white/10 bg-[#0c0c0c]">
            <div
              className="aspect-[16/9] bg-[#141414] overflow-hidden relative group cursor-zoom-in"
              onClick={() => setZoomedImg({ src: IMG_BOWTIE, alt: "Effet Bow-tie sur un diamant taille ovale" })}
              data-testid="open-bowtie-zoom"
            >
              <img src={IMG_BOWTIE} alt="Bow-tie effect" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 border border-[#D4AF37]/60 px-4 py-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  <ZoomIn className="h-3.5 w-3.5" /> Agrandir la photo
                </div>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Défaut 02 · Bow-tie</span>
              </div>
              <h3 className="font-serif text-xl mt-3 text-white">Le « nœud papillon » — une ombre au centre</h3>
              <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                Zone sombre en forme de papillon au centre de la pierre, causée par un <strong className="text-white">angle de pavillon mal calculé</strong>
                qui bloque la lumière. Présent à des degrés variables sur <strong className="text-white">toutes les tailles élongées</strong> —
                l'enjeu est sa <em>visibilité</em>. Plus il est marqué, plus la pierre perd de sa valeur.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-[#A1A1AA]">
                <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span>Quasi-systématique sur <span className="text-white">Oval, Marquise, Pear, Heart, Radiant</span>.</span></li>
                <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span>Acceptable s'il est <span className="text-white">léger et uniforme</span>. Rédhibitoire s'il est noir et asymétrique.</span></li>
                <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span>Invisible sur certif GIA : <span className="text-white">il faut voir la pierre en main</span> sous lumière diffuse.</span></li>
              </ul>
            </div>
          </div>
        </div>

        <Callout icon={Eye} title="Le réflexe de l'intermédiaire">
          Avant toute négociation sur une <strong className="text-white">taille fantaisie</strong> (Oval, Emerald, Marquise, Pear),
          exigez une vidéo en main sous lumière neutre — pas seulement le certif GIA. Le GIA note la <em>proportion</em> de la taille,
          pas son rendu visuel. Le windowing et le bow-tie ne se voient qu'en observation directe.
        </Callout>
      </Section>

      <Section eyebrow="Certificats" title="GIA vs IGI : lequel privilégier ?" index="03">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <CertCard
            name="GIA"
            sub="Gemological Institute of America · 1931"
            bullets={[
              "Créateur des 4C — la référence mondiale",
              "Les plus sévères sur Color et Clarity",
              "Pierre GIA-certifiée : +8 à +15 % vs équivalent non-GIA",
              "Incontournable pour diamants >1 ct et naturels",
            ]}
            recommended
          />
          <CertCard
            name="IGI"
            sub="International Gemological Institute · 1975"
            bullets={[
              "Dominant sur le marché diamants de laboratoire (LGD)",
              "Plus souple, notations parfois plus généreuses (+0,5 grade)",
              "Largement utilisé à Anvers et en Inde",
              "Très fréquent sur les diamants 0,3–0,8 ct",
            ]}
          />
        </div>
        <Callout icon={Award} title="Règle pratique">
          Pour un client fortuné ou un deal d'investissement : <strong className="text-white">toujours GIA</strong>. Pour
          un stock commercial ou des pierres {"<"}1 ct sous marque retail : IGI est acceptable. HRD (Anvers) est l'alternative
          européenne historique, également reconnue.
        </Callout>

        {/* GIA Certificate mockup */}
        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Exemple · GIA Grading Report</div>
              <div className="font-serif text-xl mt-1 text-white">À quoi ressemble un certificat GIA</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B]">Specimen pédagogique</div>
          </div>

          <div className="border border-[#D4AF37]/40 bg-white text-black overflow-hidden shadow-2xl">
            <div className="bg-[#002f5f] text-white px-8 py-5 flex items-center justify-between border-b-4 border-[#D4AF37]">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Gemological Institute of America</div>
                <div className="font-serif text-2xl mt-1">GIA Diamond Grading Report</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Report number</div>
                <div className="font-mono text-lg mt-1">2141438291</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3">
              <div className="md:col-span-2 p-8 border-r border-gray-200">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#002f5f] mb-4">Grading Results</div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
                  <GiaRow label="Shape and Cutting Style" value="Round Brilliant" />
                  <GiaRow label="Measurements" value="6.50 – 6.53 × 4.02 mm" />
                  <GiaRow label="Carat Weight" value="1.03 carat" bold />
                  <GiaRow label="Color Grade" value="G" bold />
                  <GiaRow label="Clarity Grade" value="VS2" bold />
                  <GiaRow label="Cut Grade" value="Excellent" bold />
                </div>

                <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-[#002f5f] mb-4">Additional Grading Information</div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <GiaRow label="Polish" value="Excellent" />
                  <GiaRow label="Symmetry" value="Very Good" />
                  <GiaRow label="Fluorescence" value="None" />
                  <GiaRow label="Inscription(s)" value={`"GIA 2141438291"`} />
                </div>

                <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-[#002f5f] mb-3">Proportions</div>
                <div className="flex items-end gap-4">
                  {/* Proportions diagram (pavilion + crown) */}
                  <svg viewBox="0 0 160 110" className="h-28 w-40">
                    {/* Table */}
                    <line x1="30" y1="20" x2="130" y2="20" stroke="#002f5f" strokeWidth="1.5" />
                    <text x="80" y="14" fontSize="8" fill="#002f5f" textAnchor="middle">57%</text>
                    {/* Crown */}
                    <line x1="30" y1="20" x2="10" y2="45" stroke="#002f5f" strokeWidth="1.5" />
                    <line x1="130" y1="20" x2="150" y2="45" stroke="#002f5f" strokeWidth="1.5" />
                    <line x1="10" y1="45" x2="150" y2="45" stroke="#002f5f" strokeWidth="1.5" />
                    <text x="80" y="38" fontSize="8" fill="#002f5f" textAnchor="middle">15.0%</text>
                    {/* Pavilion */}
                    <line x1="10" y1="45" x2="80" y2="100" stroke="#002f5f" strokeWidth="1.5" />
                    <line x1="150" y1="45" x2="80" y2="100" stroke="#002f5f" strokeWidth="1.5" />
                    <text x="80" y="80" fontSize="8" fill="#002f5f" textAnchor="middle">43.0%</text>
                    {/* Labels */}
                    <text x="3" y="103" fontSize="6" fill="#52525B">Depth 62.0%</text>
                  </svg>
                  <div className="text-xs text-gray-700 space-y-1">
                    <div>Table: <span className="font-mono text-[#002f5f]">57 %</span></div>
                    <div>Crown Angle: <span className="font-mono text-[#002f5f]">34.5°</span></div>
                    <div>Pavilion Angle: <span className="font-mono text-[#002f5f]">40.8°</span></div>
                    <div>Star Length: <span className="font-mono text-[#002f5f]">55 %</span></div>
                    <div>Girdle: <span className="font-mono text-[#002f5f]">Medium to Slightly Thick</span></div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-gray-50">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#002f5f] mb-4">Clarity Characteristics</div>
                {/* Plotting diagram: circular with inclusion markers */}
                <svg viewBox="0 0 140 140" className="w-full max-w-[180px] mx-auto">
                  <circle cx="70" cy="70" r="60" fill="none" stroke="#002f5f" strokeWidth="1.5" />
                  <circle cx="70" cy="70" r="40" fill="none" stroke="#002f5f" strokeWidth="0.8" />
                  {Array.from({ length: 8 }).map((_, i) => {
                    const a = (i * Math.PI) / 4;
                    return <line key={i} x1="70" y1="70" x2={70 + Math.cos(a) * 60} y2={70 + Math.sin(a) * 60} stroke="#002f5f" strokeOpacity="0.4" strokeWidth="0.5" />;
                  })}
                  {/* Inclusions */}
                  <circle cx="88" cy="58" r="2" fill="#D4AF37" />
                  <path d="M 55 85 L 62 88 L 58 82 Z" fill="#D4AF37" />
                  <circle cx="48" cy="60" r="1.5" fill="#D4AF37" />
                </svg>
                <div className="mt-4 text-xs space-y-1.5 text-gray-700">
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#D4AF37]" /> Crystal</div>
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#D4AF37]" /> Feather</div>
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#D4AF37]" /> Pinpoint</div>
                </div>
                <p className="mt-4 text-[10px] text-gray-500 italic">Plotting diagram. Symbols indicate type and approximate position of clarity characteristics.</p>
              </div>
            </div>

            <div className="bg-gray-100 px-8 py-4 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-gray-600 border-t border-gray-200">
              <span>Date of issue · November 18, 2025</span>
              <span className="text-[#002f5f]">www.gia.edu · verify with report number</span>
            </div>
          </div>

          <Callout icon={Eye} title="Lecture rapide d'un GIA">
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong className="text-white">Shape</strong> et <strong className="text-white">Measurements</strong> définissent physiquement la pierre.</li>
              <li><strong className="text-white">Carat · Color · Clarity · Cut</strong> = les 4 C — la cotation Rapaport se lit dans cet ordre exact.</li>
              <li><strong className="text-white">Polish / Symmetry / Fluorescence</strong> : 3 grades supplémentaires qui font basculer le prix de ±5 %.</li>
              <li><strong className="text-white">Report number</strong> gravé au laser sur le rondiste : vérifiable sur gia.edu en 10 secondes. <em>Si le numéro ne match pas la pierre, c'est un faux certificat — ou pire, un vrai certificat avec une fausse pierre.</em></li>
              <li><strong className="text-white">Plotting diagram</strong> (droite) : chaque inclusion visible sous 10× est positionnée. Sert de fingerprint unique pour identifier la pierre sans ambiguïté.</li>
            </ul>
          </Callout>
        </div>
      </Section>

      {/* FANCY COLORED DIAMONDS */}
      <Section eyebrow="Diamants d'investissement" title="Fancy Colored Diamonds : la rareté absolue" index="04">
        <p>
          Moins de <strong className="text-white">0,01 % des diamants extraits</strong> présentent une couleur
          suffisamment saturée pour être classés "Fancy" par la GIA. Ce sont les pierres les plus recherchées au monde,
          dominant les records de vente aux enchères. Pour un bijoutier de renom ou un client fortuné, une
          fancy colored diamond est l'actif tangible par excellence : portable, discrète, quasi-inviolable, mondialement
          liquide.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {[
            {
              c: "Rouge", hex: "#c0392b",
              rarity: "~30 pierres >1 ct connues",
              price: "1 000 000 – 3 000 000 $/ct",
              origin: "Brésil (mine Fazenda de Estrela), Argyle (Australie, fermée 2020)",
              record: "Moussaieff Red (5,11 ct) — valeur estimée 20 M$",
              note: "LE diamant le plus rare. Couleur due à une anomalie cristalline unique (plastic deformation).",
            },
            {
              c: "Rose", hex: "#e8a0b8",
              rarity: "~1 % des fancy — Argyle 90 % source",
              price: "100 000 – 2 000 000 $/ct",
              origin: "Argyle (Australie, fermée 2020), Golconde (Inde, historique)",
              record: "CTF Pink Star (59,60 ct) — 71,2 M$ (Sotheby's 2017)",
              note: "Fermeture d'Argyle a déclenché une envolée : +40 % entre 2020 et 2024 pour les pink intense.",
              hot: true,
            },
            {
              c: "Bleu", hex: "#4a72b8",
              rarity: "~0,02 % des fancy",
              price: "200 000 – 4 000 000 $/ct",
              origin: "Cullinan (Afrique du Sud), Golconde",
              record: "Oppenheimer Blue (14,62 ct) — 57,5 M$ (Christie's 2016)",
              note: "Couleur due au bore. Seule mine active : Cullinan Diamond Mine (Petra Diamonds).",
            },
            {
              c: "Vert", hex: "#6fa86f",
              rarity: "Extrêmement rare — <10 pierres/an >1 ct",
              price: "150 000 – 3 000 000 $/ct",
              origin: "Afrique Centrale, Brésil, historiquement Inde",
              record: "Dresden Green (40,70 ct) — inestimable, collection musée",
              note: "Couleur naturellement induite par radioactivité géologique. Pratiquement toujours Fancy Intense.",
            },
            {
              c: "Jaune", hex: "#f5d046",
              rarity: "~60 % des fancy colored",
              price: "5 000 – 100 000 $/ct",
              origin: "Afrique du Sud, Sierra Leone, Australie",
              record: "Tiffany Yellow (128,54 ct) — inestimable, bijou historique",
              note: "Le plus accessible des fancy. Canary yellow très demandé. Investissement d'entrée de gamme.",
            },
            {
              c: "Marron / Cognac", hex: "#8c5a3c",
              rarity: "Courant — 15 % du marché",
              price: "1 500 – 20 000 $/ct",
              origin: "Argyle (Australie), Russie (Sibérie)",
              record: "Golden Jubilee (545,67 ct) — roi de Thaïlande",
              note: "Rebaptisé 'champagne' ou 'cognac' par De Beers pour le marketing haut de gamme. Bon rapport qualité/prix.",
            },
            {
              c: "Orange", hex: "#e08a3c",
              rarity: "~0,1 % des fancy",
              price: "50 000 – 500 000 $/ct",
              origin: "Afrique du Sud, Australie",
              record: "The Orange (14,82 ct) — 35,5 M$ (Christie's 2013)",
              note: "Appelée 'Pumpkin Orange' à saturation maximale. Très demandé par collectionneurs asiatiques.",
            },
            {
              c: "Violet / Pourpre", hex: "#6b3a7a",
              rarity: "Quelques dizaines >0,5 ct au monde",
              price: "100 000 – 1 500 000 $/ct",
              origin: "Argyle (Australie)",
              record: "The Supreme Purple Star (2–5 ct, estimation privée)",
              note: "Ultra-confidentiel. Souvent acheté en direct aux enchères privées Argyle Tender.",
            },
            {
              c: "Noir", hex: "#1a1a1a",
              rarity: "Abondant en rough — rare en qualité polie",
              price: "2 000 – 15 000 $/ct",
              origin: "Brésil, Afrique Centrale",
              record: "Black Orlov (67,50 ct) — bijou historique",
              note: "Couleur due aux inclusions de graphite. Marché joaillerie design, moins investissement pur.",
            },
          ].map((d) => (
            <div key={d.c} className={`border p-6 ${d.hot ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]" : "border-white/10 bg-[#0c0c0c]"} hover:border-[#D4AF37]/30 transition-colors`}>
              <div className="flex items-center justify-between">
                <div
                  className="h-14 w-14 rounded-full"
                  style={{
                    background: `radial-gradient(circle at 30% 30%, #ffffff 0%, ${d.hex} 45%, ${d.hex} 100%)`,
                    boxShadow: `0 0 25px ${d.hex}60, inset 0 0 20px rgba(255,255,255,0.2)`,
                  }}
                />
                {d.hot && <span className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Premium</span>}
              </div>
              <div className="font-serif text-2xl mt-4">{d.c}</div>
              <div className="mt-5 space-y-3 text-xs">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Rareté</div>
                  <div className="text-[#E4E4E7] mt-1">{d.rarity}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Prix /ct (Fancy Intense)</div>
                  <div className="font-mono text-white mt-1">{d.price}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Origines</div>
                  <div className="text-[#A1A1AA] mt-1">{d.origin}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Record</div>
                  <div className="text-[#A1A1AA] mt-1 italic">{d.record}</div>
                </div>
              </div>
              <p className="mt-4 pt-4 border-t border-white/10 text-xs text-[#C4C4C7]">{d.note}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="border border-white/10 bg-[#0c0c0c] p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Grades GIA pour diamants de couleur</div>
            <div className="font-serif text-xl mt-3">Intensité croissante</div>
            <ul className="mt-5 space-y-2 text-sm text-[#A1A1AA]">
              <li><span className="text-white font-mono">Faint</span> → couleur à peine perceptible</li>
              <li><span className="text-white font-mono">Very Light · Light</span> → couleur claire</li>
              <li><span className="text-white font-mono">Fancy Light</span> → seuil investissement</li>
              <li className="text-[#D4AF37]"><span className="font-mono">Fancy</span> → catégorie commerciale</li>
              <li className="text-[#D4AF37]"><span className="font-mono">Fancy Intense</span> → prime +30 à +80 %</li>
              <li className="text-[#D4AF37]"><span className="font-mono">Fancy Vivid</span> → sommet (prime +100 à +400 %)</li>
              <li className="text-[#D4AF37]"><span className="font-mono">Fancy Deep · Fancy Dark</span> → spécifiques</li>
            </ul>
          </div>
          <Callout icon={Award} title="Pourquoi les collectionneurs adorent les fancy">
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong className="text-white">Rareté absolue</strong> : Argyle (ex-source principale du rose) est fermée depuis novembre 2020. L'offre ne peut que baisser.</li>
              <li><strong className="text-white">Performance d'investissement</strong> : Fancy Intense Pink a fait +168 % sur 15 ans (FCRF index).</li>
              <li><strong className="text-white">Discrétion</strong> : actif tangible, portable, ne déclenche pas de seuil bancaire à la frontière.</li>
              <li><strong className="text-white">Liquidité haut de gamme</strong> : Sotheby's, Christie's, Phillips, Bonhams — réseau mondial d'enchères.</li>
              <li><strong className="text-white">Signature statutaire</strong> : pièce maîtresse de collection privée pour UHNW et maisons de joaillerie (Graff, Harry Winston, Moussaieff, Chopard).</li>
            </ul>
          </Callout>
        </div>
      </Section>

      <StoneHeader code="02" name="Rubis" tag="Corundum · Hardness 9" />

      <Section eyebrow="Sourcing" title="Birmanie, Mozambique, Thaïlande" index="05">
        <p>
          Le rubis est le <strong className="text-white">corindon rouge</strong>. Sa valeur dépend d'abord de l'origine,
          puis de la saturation de couleur, puis du traitement.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <OriginCard flag="🇲🇲" country="Birmanie (Myanmar)" detail="Mines de Mogok et Mong Hsu. Les rubis birmans 'pigeon blood' sont les plus chers au monde. Un 3 ct non-chauffé birman peut atteindre 50 000–200 000 $/ct." />
          <OriginCard flag="🇲🇿" country="Mozambique" detail="Mine de Montepuez (Gemfields). Production dominante depuis 2011. Couleur très proche du birman, prix 40–70 % moins cher. Bonne alternative commerciale." />
          <OriginCard flag="🇹🇭" country="Thaïlande" detail="Hub mondial de traitement (Chanthaburi). Historiquement source (mines Trat) aujourd'hui quasi-épuisée. Beaucoup de rubis africains chauffés à Bangkok avant revente." />
        </div>
      </Section>

      <Section eyebrow="Couleur & traitement" title="Pigeon Blood et impact du chauffage" index="06">
        <p>
          <strong className="text-white">"Pigeon Blood"</strong> : la couleur la plus recherchée. Un rouge pur, légèrement
          bleuté, avec fluorescence rouge forte. Reconnu uniquement sur rubis birmans non-chauffés par SSEF, Gübelin ou GRS.
        </p>
        <div className="flex gap-3 mt-6 flex-wrap">
          {[
            { c: "#8b1a1a", l: "Rouge sombre" },
            { c: "#b91c1c", l: "Rouge vif" },
            { c: "#dc2626", l: "Pigeon blood" },
            { c: "#ef4444", l: "Rouge framboise" },
            { c: "#f87171", l: "Rose-rouge" },
          ].map((c) => (
            <div key={c.l} className="text-center">
              <div className="h-20 w-20 rounded" style={{ backgroundColor: c.c, boxShadow: "inset 0 0 20px rgba(255,255,255,0.15)" }} />
              <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA]">{c.l}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <TreatCard
            title="Non-chauffé (No Heat)"
            tone="premium"
            body="Rubis naturel non traité thermiquement. Rarissime en qualité supérieure. Prime de +100 à +300 % vs équivalent chauffé. Mentionné explicitement sur le certificat."
          />
          <TreatCard
            title="Chauffé (Heated)"
            tone="standard"
            body="Traitement standard (1 600–1 800 °C) pour améliorer couleur et clarté. Accepté par le marché, stable, irréversible. 95 % des rubis en circulation sont chauffés."
          />
        </div>

        <Callout icon={Award} title="Certificats rubis à privilégier">
          <strong className="text-white">SSEF (Suisse)</strong>, <strong className="text-white">Gübelin (Suisse)</strong>
          et <strong className="text-white">GRS (Bangkok)</strong> sont les trois laboratoires de référence pour rubis
          haut de gamme. Un rubis avec un certificat SSEF "pigeon blood, Burma, no heat" est un investissement bancable.
          GIA certifie aussi — mais sans la mention "pigeon blood" (commerciale).
        </Callout>
      </Section>

      {/* EMERALDS */}
      <StoneHeader code="03" name="Émeraudes" tag="Beryl · Hardness 7.5–8" />

      <Section eyebrow="Sourcing" title="Colombie, Zambie, Brésil" index="07">
        <p>
          L'émeraude est un <strong className="text-white">béryl vert</strong>, coloré par le chrome ou le vanadium. Sa
          fragilité (inclusions = "jardin") est acceptée — c'est la nature même de la pierre.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <OriginCard flag="🇨🇴" country="Colombie" detail="Muzo, Chivor, Coscuez. Référence absolue. Vert pur légèrement bleuté, saturation riche. Prix premium +50 à +150 % vs autres origines." />
          <OriginCard flag="🇿🇲" country="Zambie (Kagem)" detail="Mines de Gemfields. Vert plus sombre et légèrement bleuté. Souvent plus propre que les colombiennes. Rapport qualité/prix excellent." />
          <OriginCard flag="🇧🇷" country="Brésil" detail="Minas Gerais, Bahia. Vert plus jaune, moins intense. Prix accessible. Utilisé en joaillerie commerciale." />
        </div>
      </Section>

      <Section eyebrow="Traitement" title="Huile et cèdre : le secret de l'émeraude" index="08">
        <p>
          Quasiment 100 % des émeraudes commerciales sont traitées à l'huile de cèdre pour masquer les fissures
          ouvertes en surface. Le <strong className="text-white">degré de traitement</strong> impacte massivement le prix.
        </p>
        <div className="mt-8 space-y-3">
          {[
            { g: "None", label: "Aucun traitement", impact: "+200 à +400 %", desc: "Pierre vierge. Rarissime. Presque uniquement de la collection." },
            { g: "Insignificant", label: "Traitement insignifiant", impact: "+50 à +100 %", desc: "Traces d'huile à peine détectables. Haut de gamme." },
            { g: "Minor", label: "Traitement mineur", impact: "Prix standard", desc: "Huile dans quelques fissures. Acceptable commercialement." },
            { g: "Moderate", label: "Traitement modéré", impact: "−15 à −25 %", desc: "Beaucoup de fissures traitées. Qualité moyenne." },
            { g: "Significant", label: "Traitement important", impact: "−40 à −60 %", desc: "Fissures majeures bouchées. Pierre fragile visuellement." },
          ].map((t) => (
            <div key={t.g} className="grid grid-cols-12 border border-white/10 bg-[#0c0c0c] p-5">
              <div className="col-span-12 md:col-span-2 font-mono text-[#D4AF37]">{t.g}</div>
              <div className="col-span-12 md:col-span-4 font-serif text-white mt-2 md:mt-0">{t.label}</div>
              <div className="col-span-12 md:col-span-2 font-mono text-[#D4AF37] mt-2 md:mt-0 text-sm">{t.impact}</div>
              <div className="col-span-12 md:col-span-4 text-xs text-[#A1A1AA] mt-2 md:mt-0">{t.desc}</div>
            </div>
          ))}
        </div>

        <Callout icon={Award} title="Certificats émeraude">
          <strong className="text-white">AGL (American Gemological Laboratories)</strong> est la référence absolue — seul à
          mesurer précisément le degré de traitement (F1, F2, F3). <strong className="text-white">Gübelin</strong> et
          <strong className="text-white"> SSEF</strong> sont aussi de premier plan. Exigez toujours un certificat indiquant
          origine <em>et</em> traitement.
        </Callout>
      </Section>

      {/* SAPPHIRES */}
      <StoneHeader code="04" name="Saphirs" tag="Corundum · Hardness 9" />

      <Section eyebrow="Couleur & origine" title="Royal Blue et Ceylon : les deux stars" index="09">
        <p>
          Le saphir est lui aussi du corindon, mais bleu (présence de fer + titane). Deux origines dominent le haut de gamme.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Cachemire (historique)</div>
            <div className="font-serif text-2xl text-white mt-2">Royal Blue</div>
            <div className="mt-5 h-28 rounded" style={{ background: "radial-gradient(circle at 30% 30%, #1e3a8a, #0f1e3a 70%, #050a1a)" }} />
            <p className="mt-5 text-sm text-[#A1A1AA]">
              Bleu profond, légèrement violet, sans zonage. Cachemire (Inde, mines épuisées) : 100 000+ $/ct. Birmanie
              (Mogok) et Madagascar (Ilakaka) produisent également du Royal Blue — plus accessibles.
            </p>
          </div>
          <div className="border border-white/10 bg-[#0c0c0c] p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Sri Lanka</div>
            <div className="font-serif text-2xl text-white mt-2">Ceylon</div>
            <div className="mt-5 h-28 rounded" style={{ background: "radial-gradient(circle at 30% 30%, #3b82f6, #1e40af 70%, #0a1f3a)" }} />
            <p className="mt-5 text-sm text-[#A1A1AA]">
              Bleu plus vif et lumineux, souvent appelé "cornflower blue". Origine la plus productive en haut de gamme.
              Excellent rapport qualité/prix pour Royal Blue-like à 5 000–20 000 $/ct.
            </p>
          </div>
        </div>

        <Callout icon={Award} title="Certificats saphir">
          <strong className="text-white">SSEF</strong>, <strong className="text-white">Gübelin</strong> et
          <strong className="text-white"> AGL</strong> sont les trois laboratoires de référence. Un saphir non-chauffé avec
          mention "Royal Blue" ou "Cornflower Blue" sur certificat SSEF = prime de +40 à +80 %. Le chauffage est le seul
          traitement accepté sans décote majeure — à condition qu'il soit déclaré.
        </Callout>
      </Section>

      {/* Documents */}
      <Section eyebrow="Ressources" title="Points clés pour un deal pierres précieuses" index="10">
        <p>
          Pour les gemmes, la clé est <strong className="text-white">le certificat de laboratoire</strong> (GIA, SSEF,
          Gübelin, AGL, GRS). Pas de certificat = pas de deal. Les pierres fantaisie (fancy colored diamonds, rubis
          no-heat, saphirs Kashmir) se négocient exclusivement sur certificat vérifié en main — numéro laser gravé sur
          le rondiste, croisé avec la base de données en ligne du laboratoire.
        </p>
        <ul className="mt-6 space-y-3 text-[#A1A1AA]">
          <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span><span className="text-white">Toujours exiger le certificat original</span> (pas une copie scannée) — ou à minima vérifier le numéro sur le site du laboratoire.</span></li>
          <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span><span className="text-white">Pour les diamants {">"} 1 ct</span> : GIA obligatoire. HRD ou IGI en second choix.</span></li>
          <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span><span className="text-white">Pour les rubis et saphirs haut de gamme</span> : SSEF ou Gübelin pour la mention origine + no-heat.</span></li>
          <li className="flex gap-3"><span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" /><span><span className="text-white">Pour les émeraudes</span> : AGL pour le degré de traitement (F1 à F3). Critique pour la valeur finale.</span></li>
        </ul>
      </Section>

      <Section eyebrow="Rolodex" title="Fournisseurs & partenaires — Pierres précieuses" index="09">
        <p>
          Le réseau opérationnel : <strong className="text-white">perles de culture, émeraudes, rubis, saphirs, diamants blancs et colorés</strong> — plus les équipementiers et labs.
          WhatsApp cliquable pour contact instantané.
        </p>

        <h3 className="mt-10 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Perles de culture
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SupplierCard
            name="Oyster Bay Gems"
            tag="Perles de culture"
            location="Dubai, UAE"
            whatsapps={["+971 55 956 6908", "+971 50 108 7725"]}
          />
        </div>

        <h3 className="mt-12 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Émeraudes, Rubis, Saphirs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SupplierCard
            name="DERS International"
            tag="Multi-sourcing gemmes"
            location="Thailand · Hong Kong"
            whatsapps={["+66 81 823 3425", "+852 6153 7062"]}
            website="https://www.dersintl.com"
          />
          <SupplierCard
            name="Beautiful Color Stone Co. Ltd"
            tag="Fine Colombian emeralds"
            location="Jewelry Trade Center, Bangkok, Thailand"
            whatsapps={["+66 89 688 5134"]}
            instagram="avi.emeralds"
          />
          <SupplierCard
            name="Zayn Dubai"
            tag="Fine emeralds, rubies & sapphires"
            location="Dubai, UAE"
            whatsapps={["+91 93513 24156"]}
          />
          <SupplierCard
            name="RAMIGEM"
            tag="Fine emeralds & colored diamonds"
            location="New York, USA"
            whatsapps={["+1 201 957 3247"]}
          />
          <SupplierCard
            name="Gem Centre"
            tag="Multi-gemmes"
            location="Hong Kong"
            whatsapps={["+852 9350 8996"]}
          />
          <SupplierCard
            name="Gem Corp FZCO"
            tag="Multi-gemmes · Free zone DMCC"
            location="Dubai, UAE"
            whatsapps={["+971 50 209 4728"]}
          />
        </div>

        <h3 className="mt-12 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Diamants
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SupplierCard
            name="Bhavna Gems"
            tag="Diamantaire · RapNet ID 81517"
            location="Mumbai, India"
            whatsapps={["+91 70169 30623"]}
          />
          <SupplierCard
            name="Anita Diamonds"
            tag="Diamantaire international"
            location="Belgium · Dubai · Mumbai · Hong Kong"
            whatsapps={["+32 487 77 70 00"]}
            instagram="anita.diamonds"
          />
          <SupplierCard
            name="KGK Diamonds"
            tag="Groupe diamantaire"
            location="Dubai, UAE"
            whatsapps={["+971 56 619 5144"]}
            website="https://www.kgkgroup.com"
          />
          <SupplierCard
            name="Diakraft DMCC"
            tag="Colored diamonds (groupe Fancy Diam)"
            location="Dubai · Belgium · New York · Israel · Italy · HK"
            whatsapps={["+971 524 812 300"]}
            website="https://www.fancydiamgroup.com"
          />
          <SupplierCard
            name="El Etany Trading Ltd"
            tag="Colored & white diamonds"
            location="Israel"
            whatsapps={["+972 54 397 4267", "+49 172 216 4088"]}
          />
          <SupplierCard
            name="RA Diamond & Jewellery LLC"
            tag="Diamantaire"
            location="Dubai, UAE · India"
            whatsapps={["+971 50 950 6967", "+971 55 776 2691"]}
            website="https://www.radiamond.in"
          />
          <SupplierCard
            name="Anu Diam"
            tag="Diamantaire"
            location="Dubai, UAE"
            whatsapps={["+971 58 573 3101"]}
          />
          <SupplierCard
            name="Shrey International"
            tag="Jewellery & diamonds"
            location="Dubai, UAE"
            whatsapps={["+971 55 266 4551"]}
            instagram="shrey_jewels"
          />
          <SupplierCard
            name="Paladiya Brothers & Co"
            tag="Diamantaire"
            location="India"
            whatsapps={["+91 97697 83503"]}
          />
        </div>

        <h3 className="mt-12 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Équipement & laboratoires
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SupplierCard
            name="Smartpro"
            tag="Équipement diamantaire · loupes & machines"
            location="Thailand"
            whatsapps={["+66 80 560 4359"]}
            website="https://www.smartproinstrument.com"
          />
          <SupplierCard
            name="Diacam360"
            tag="Imagerie diamant 360°"
            location="Israel"
            whatsapps={["+972 3 613 2138"]}
            website="https://www.diacam360.com"
          />
        </div>
      </Section>

      {/* Image lightbox */}
      {zoomedImg && (
        <div
          data-testid="image-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          onClick={() => setZoomedImg(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setZoomedImg(null); }}
            data-testid="close-lightbox"
            className="absolute top-5 right-5 p-3 border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={zoomedImg.src}
            alt={zoomedImg.alt}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <img src={IMG_DIAMOND} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Gem className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Secteur · 03</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        Les <em className="italic text-[#F5F5DC]">Pierres précieuses</em> — <span className="text-[#D4AF37]">quatre</span> stars.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Diamants, rubis, émeraudes, saphirs. Histoire, origine, grades, certificats et formation des prix —
        l'expertise qui sépare l'amateur du broker.
      </p>
    </div>
  </section>
);

const StoneHeader = ({ code, name, tag }) => (
  <div className="flex items-end justify-between mb-10 pb-4 border-b border-[#D4AF37]/30">
    <div className="flex items-baseline gap-5">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{code}</span>
      <h2 className="font-serif text-3xl md:text-5xl font-light">{name}</h2>
    </div>
    <span className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA] hidden md:block">{tag}</span>
  </div>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-16">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h3 className="font-serif text-xl md:text-2xl lg:text-3xl font-light mt-2 leading-tight">{title}</h3>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const SubSection = ({ title, icon: Icon, children }) => (
  <div className="mt-12 first:mt-0">
    <div className="flex items-center gap-3 mb-5">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="font-serif text-xl text-white">{title}</span>
    </div>
    <div className="text-[#C4C4C7] text-sm leading-relaxed">{children}</div>
  </div>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const RefCard = ({ title, sub, body }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-[#D4AF37]/30 transition-colors">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{sub}</div>
    <div className="font-serif text-xl text-white mt-2">{title}</div>
    <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

const GiaRow = ({ label, value, bold }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.22em] text-gray-500">{label}</div>
    <div className={`mt-1 ${bold ? "font-mono text-[#002f5f] text-base font-semibold" : "text-gray-700 text-sm"}`}>{value}</div>
  </div>
);

const CertCard = ({ name, sub, bullets, recommended }) => (
  <div className={`border p-7 ${recommended ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]" : "border-white/10 bg-[#0c0c0c]"}`}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">{sub}</span>
      {recommended && <span className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Recommandé</span>}
    </div>
    <div className="font-serif text-3xl text-white mt-3">{name}</div>
    <ul className="mt-6 space-y-2 text-sm text-[#A1A1AA]">
      {bullets.map((b, i) => <li key={i} className="flex gap-2"><span className="text-[#D4AF37]">·</span><span>{b}</span></li>)}
    </ul>
  </div>
);

const OriginCard = ({ flag, country, detail }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-white/25 transition-colors">
    <div className="text-2xl">{flag}</div>
    <div className="font-serif text-lg mt-2 text-white">{country}</div>
    <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">{detail}</p>
  </div>
);

const TreatCard = ({ title, tone, body }) => (
  <div className={`border p-6 ${tone === "premium" ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]" : "border-white/10 bg-[#0c0c0c]"}`}>
    <div className="flex items-center gap-3">
      <Flame className="h-4 w-4 text-[#D4AF37]" />
      <div className="font-serif text-lg text-white">{title}</div>
    </div>
    <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

// ---------- Cut shapes (SVG) ----------
const CutCard = ({ name, desc, shape }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-5 hover:border-[#D4AF37]/30 transition-colors text-center">
    <div className="h-24 flex items-center justify-center">
      <CutShape shape={shape} />
    </div>
    <div className="font-serif text-base mt-3 text-white">{name}</div>
    <div className="text-xs text-[#A1A1AA] mt-2">{desc}</div>
  </div>
);

const CutShape = ({ shape }) => {
  const gradId = `grad-${shape}`;
  const Defs = () => (
    <defs>
      <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="60%" stopColor="#d4d4d4" />
        <stop offset="100%" stopColor="#52525B" />
      </linearGradient>
    </defs>
  );
  const stroke = "#D4AF37";
  const strokeWidth = 1;
  switch (shape) {
    case "round":
      return (
        <svg viewBox="0 0 80 80" className="h-20 w-20"><Defs />
          <circle cx="40" cy="40" r="32" fill={`url(#${gradId})`} stroke={stroke} strokeWidth={strokeWidth} />
          <circle cx="40" cy="40" r="18" fill="none" stroke={stroke} strokeOpacity="0.5" strokeWidth={strokeWidth} />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * Math.PI) / 4;
            return <line key={i} x1="40" y1="40" x2={40 + Math.cos(a) * 32} y2={40 + Math.sin(a) * 32} stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />;
          })}
        </svg>
      );
    case "princess":
      return (
        <svg viewBox="0 0 80 80" className="h-20 w-20"><Defs />
          <rect x="12" y="12" width="56" height="56" fill={`url(#${gradId})`} stroke={stroke} strokeWidth={strokeWidth} />
          <line x1="12" y1="12" x2="68" y2="68" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
          <line x1="68" y1="12" x2="12" y2="68" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
        </svg>
      );
    case "emerald":
      return (
        <svg viewBox="0 0 80 80" className="h-20 w-20"><Defs />
          <polygon points="20,14 60,14 68,26 68,54 60,66 20,66 12,54 12,26" fill={`url(#${gradId})`} stroke={stroke} strokeWidth={strokeWidth} />
          <polygon points="26,22 54,22 58,30 58,50 54,58 26,58 22,50 22,30" fill="none" stroke={stroke} strokeOpacity="0.45" strokeWidth={strokeWidth} />
          <polygon points="32,30 48,30 50,34 50,46 48,50 32,50 30,46 30,34" fill="none" stroke={stroke} strokeOpacity="0.35" strokeWidth={strokeWidth} />
        </svg>
      );
    case "cushion":
      return (
        <svg viewBox="0 0 80 80" className="h-20 w-20"><Defs />
          <rect x="12" y="12" rx="16" ry="16" width="56" height="56" fill={`url(#${gradId})`} stroke={stroke} strokeWidth={strokeWidth} />
          <line x1="12" y1="12" x2="68" y2="68" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
          <line x1="68" y1="12" x2="12" y2="68" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
        </svg>
      );
    case "radiant":
      return (
        <svg viewBox="0 0 80 80" className="h-20 w-20"><Defs />
          <polygon points="20,12 60,12 68,20 68,60 60,68 20,68 12,60 12,20" fill={`url(#${gradId})`} stroke={stroke} strokeWidth={strokeWidth} />
          <polygon points="28,20 52,20 60,28 60,52 52,60 28,60 20,52 20,28" fill="none" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
          <line x1="28" y1="20" x2="52" y2="60" stroke={stroke} strokeOpacity="0.3" strokeWidth={strokeWidth} />
          <line x1="52" y1="20" x2="28" y2="60" stroke={stroke} strokeOpacity="0.3" strokeWidth={strokeWidth} />
        </svg>
      );
    case "marquise":
      return (
        <svg viewBox="0 0 80 80" className="h-20 w-20"><Defs />
          <path d="M40 8 C62 22, 62 58, 40 72 C18 58, 18 22, 40 8 Z" fill={`url(#${gradId})`} stroke={stroke} strokeWidth={strokeWidth} />
          <line x1="40" y1="8" x2="40" y2="72" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
          <path d="M40 16 C56 28, 56 52, 40 64 C24 52, 24 28, 40 16 Z" fill="none" stroke={stroke} strokeOpacity="0.4" strokeWidth={strokeWidth} />
        </svg>
      );
    default:
      return null;
  }
};

// Reusable supplier/contact card
const SupplierCard = ({ name, tag, location, whatsapps = [], website, instagram, phone }) => (
  <div data-testid={`supplier-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-white/10 bg-[#0c0c0c] p-6 md:p-7 hover:border-[#D4AF37]/50 transition-colors flex flex-col">
    <div className="flex items-center gap-3">
      <Users className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{tag}</span>
    </div>
    <h3 className="font-serif text-xl mt-3 text-white">{name}</h3>
    {location && (
      <div className="mt-2 flex items-center gap-2 text-xs text-[#A1A1AA]">
        <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span>{location}</span>
      </div>
    )}
    <div className="mt-5 space-y-2.5 text-sm">
      {whatsapps.map((w) => (
        <a
          key={w}
          href={`https://wa.me/${w.replace(/\D/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#25D366] transition-colors group"
        >
          <MessageCircle className="h-4 w-4 shrink-0 text-[#25D366] group-hover:scale-110 transition-transform" />
          <span className="font-mono tracking-tight">{w}</span>
        </a>
      ))}
      {phone && (
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
        >
          <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="font-mono tracking-tight">{phone}</span>
        </a>
      )}
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors break-all"
        >
          <Globe className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="truncate">{website.replace(/^https?:\/\//, "")}</span>
        </a>
      )}
      {instagram && (
        <a
          href={`https://instagram.com/${instagram.replace(/^@/, "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#E1306C] transition-colors"
        >
          <Instagram className="h-4 w-4 shrink-0 text-[#E1306C]" />
          <span>@{instagram.replace(/^@/, "")}</span>
        </a>
      )}
    </div>
  </div>
);

